import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { api, type PlaylistItem } from '@/services/api';

// Types
export type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused';

export interface CurrentTrack {
  id: string;
  songId: string;
  voicePartId?: string;
  title: string;
  artist?: string;
  voicePart?: string;
  coverUrl?: string;
  audioUrl: string;
  lrcUrl?: string;
  cues?: Cue[];
}

export interface Cue {
  id: string;
  name: string;
  timestamp: number;
  isCentral: boolean;
}

export interface LoopState {
  pointA: number | null;
  pointB: number | null;
  active: boolean;
}

interface PlayerContextType {
  // Current track
  currentTrack: CurrentTrack | null;

  // Playback state
  playbackState: PlaybackState;
  currentTime: number;
  duration: number;
  playbackRate: number;
  volume: number;

  // Loop state
  loop: LoopState;

  // Playlist state
  playlist: PlaylistItem[] | null;
  playlistIndex: number;
  playlistRepeat: boolean;

  // Player visibility
  isPlayerVisible: boolean;
  isFullScreen: boolean;

  // Actions
  play: (track: CurrentTrack) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  seek: (time: number) => void;
  seekRelative: (delta: number) => void;
  setPlaybackRate: (rate: number) => void;
  setVolume: (volume: number) => void;

  // Loop actions
  setLoopPointA: () => void;
  setLoopPointB: () => void;
  clearLoop: () => void;

  // Playlist actions
  playPlaylist: (items: PlaylistItem[], startIndex?: number) => void;
  playNext: () => void;
  playPrevious: () => void;
  setPlaylistRepeat: (value: boolean) => void;
  clearPlaylist: () => void;

  // UI actions
  setFullScreen: (value: boolean) => void;
  closePlayer: () => void;

  // Audio ref for direct access if needed
  audioRef: React.RefObject<HTMLAudioElement>;
}

const PlayerContext = createContext<PlayerContextType | null>(null);

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5];

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null);

  // Track state
  const [currentTrack, setCurrentTrack] = useState<CurrentTrack | null>(null);

  // Playback state
  const [playbackState, setPlaybackState] = useState<PlaybackState>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [volume, setVolumeState] = useState(1);

  // Loop state
  const [loop, setLoop] = useState<LoopState>({
    pointA: null,
    pointB: null,
    active: false,
  });

  // UI state
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Playlist state
  const [playlist, setPlaylist] = useState<PlaylistItem[] | null>(null);
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [playlistRepeat, setPlaylistRepeatState] = useState(false);

  // Audio event handlers
  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    setCurrentTime(audio.currentTime);

    // A-B repeat logic
    if (loop.active && loop.pointB !== null) {
      if (audio.currentTime >= loop.pointB) {
        audio.currentTime = loop.pointA ?? 0;
      }
    }
  }, [loop.active, loop.pointA, loop.pointB]);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setDuration(audio.duration);
      setPlaybackState('paused');
    }
  }, []);

  const handleEnded = useCallback(() => {
    // If A-B loop is active, restart from A
    if (loop.active && loop.pointA !== null) {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = loop.pointA;
        audio.play();
        setPlaybackState('playing');
      }
      return;
    }

    // If in playlist mode, try to play next
    if (playlist && playlist.length > 0) {
      const nextIndex = playlistIndex + 1;
      if (nextIndex < playlist.length) {
        // Auto-advance to next track
        const item = playlist[nextIndex];
        const files = item.song.files || [];
        const audioFile = files.find(f => f.voicePartId === item.voicePart.id) || files[0];

        if (audioFile) {
          const track: CurrentTrack = {
            id: `${item.song.id}-${item.voicePart.id}`,
            songId: item.song.id,
            voicePartId: item.voicePart.id,
            title: item.song.title,
            artist: item.song.artist || undefined,
            voicePart: item.voicePart.name,
            coverUrl: item.song.coverImage || undefined,
            audioUrl: api.getAuthenticatedFileUrl(audioFile.id),
          };

          const audio = audioRef.current;
          if (audio) {
            setCurrentTrack(track);
            setCurrentTime(0);
            setDuration(0);
            setLoop({ pointA: null, pointB: null, active: false });
            setPlaybackState('loading');
            setPlaylistIndex(nextIndex);

            audio.src = track.audioUrl;
            audio.load();
            audio.play().catch(err => console.error('Failed to auto-play next:', err));
          }
          return;
        }
      } else if (playlistRepeat && playlist.length > 0) {
        // Repeat playlist from beginning
        const item = playlist[0];
        const files = item.song.files || [];
        const audioFile = files.find(f => f.voicePartId === item.voicePart.id) || files[0];

        if (audioFile) {
          const track: CurrentTrack = {
            id: `${item.song.id}-${item.voicePart.id}`,
            songId: item.song.id,
            voicePartId: item.voicePart.id,
            title: item.song.title,
            artist: item.song.artist || undefined,
            voicePart: item.voicePart.name,
            coverUrl: item.song.coverImage || undefined,
            audioUrl: api.getAuthenticatedFileUrl(audioFile.id),
          };

          const audio = audioRef.current;
          if (audio) {
            setCurrentTrack(track);
            setCurrentTime(0);
            setDuration(0);
            setLoop({ pointA: null, pointB: null, active: false });
            setPlaybackState('loading');
            setPlaylistIndex(0);

            audio.src = track.audioUrl;
            audio.load();
            audio.play().catch(err => console.error('Failed to repeat playlist:', err));
          }
          return;
        }
      }
    }

    // Default: just pause
    setPlaybackState('paused');
  }, [loop.active, loop.pointA, playlist, playlistIndex, playlistRepeat]);

  const handlePlay = useCallback(() => setPlaybackState('playing'), []);
  const handlePause = useCallback(() => setPlaybackState('paused'), []);
  const handleWaiting = useCallback(() => setPlaybackState('loading'), []);
  const handleCanPlay = useCallback(() => {
    if (playbackState === 'loading') {
      setPlaybackState('paused');
    }
  }, [playbackState]);

  // Playback controls
  const play = useCallback(async (track: CurrentTrack) => {
    const audio = audioRef.current;
    if (!audio) return;

    // If same track, just resume
    if (currentTrack?.id === track.id) {
      try {
        await audio.play();
      } catch (err) {
        console.error('Failed to play:', err);
      }
      return;
    }

    // New track
    setCurrentTrack(track);
    setCurrentTime(0);
    setDuration(0);
    setLoop({ pointA: null, pointB: null, active: false });
    setIsPlayerVisible(true);
    setPlaybackState('loading');

    audio.src = track.audioUrl;
    audio.load();

    try {
      await audio.play();
    } catch (err) {
      console.error('Failed to play:', err);
    }
  }, [currentTrack]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const resume = useCallback(async () => {
    try {
      await audioRef.current?.play();
    } catch (err) {
      console.error('Failed to resume:', err);
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = loop.pointA ?? 0;
    setPlaybackState('paused');
  }, [loop.pointA]);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, Math.min(duration, time));
  }, [duration]);

  const seekRelative = useCallback((delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + delta));
  }, [duration]);

  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.playbackRate = rate;
    setPlaybackRateState(rate);
  }, []);

  const setVolume = useCallback((vol: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = vol;
    setVolumeState(vol);
  }, []);

  // Loop controls
  const setLoopPointA = useCallback(() => {
    const newA = currentTime;
    setLoop(prev => ({
      ...prev,
      pointA: newA,
      pointB: prev.pointB !== null && newA >= prev.pointB ? null : prev.pointB,
      active: false,
    }));
  }, [currentTime]);

  const setLoopPointB = useCallback(() => {
    if (loop.pointA === null) return;
    if (currentTime <= loop.pointA) return;

    setLoop(prev => ({
      ...prev,
      pointB: currentTime,
      active: true,
    }));
  }, [currentTime, loop.pointA]);

  const clearLoop = useCallback(() => {
    setLoop({
      pointA: null,
      pointB: null,
      active: false,
    });
  }, []);

  // UI controls
  const setFullScreen = useCallback((value: boolean) => {
    setIsFullScreen(value);
  }, []);

  const closePlayer = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    setCurrentTrack(null);
    setIsPlayerVisible(false);
    setIsFullScreen(false);
    setPlaybackState('idle');
    setPlaylist(null);
    setPlaylistIndex(0);
  }, []);

  // Playlist controls
  const playTrackFromPlaylist = useCallback(async (items: PlaylistItem[], index: number) => {
    const item = items[index];
    if (!item) return;

    // Find the audio file for this voice part
    const files = item.song.files || [];
    const audioFile = files.find(f => f.voicePartId === item.voicePart.id) || files[0];

    if (!audioFile) {
      console.error('No audio file found for playlist item');
      return;
    }

    const track: CurrentTrack = {
      id: `${item.song.id}-${item.voicePart.id}`,
      songId: item.song.id,
      voicePartId: item.voicePart.id,
      title: item.song.title,
      artist: item.song.artist || undefined,
      voicePart: item.voicePart.name,
      coverUrl: item.song.coverImage || undefined,
      audioUrl: api.getAuthenticatedFileUrl(audioFile.id),
    };

    const audio = audioRef.current;
    if (!audio) return;

    setCurrentTrack(track);
    setCurrentTime(0);
    setDuration(0);
    setLoop({ pointA: null, pointB: null, active: false });
    setIsPlayerVisible(true);
    setPlaybackState('loading');
    setPlaylistIndex(index);

    audio.src = track.audioUrl;
    audio.load();

    try {
      await audio.play();
    } catch (err) {
      console.error('Failed to play:', err);
    }
  }, []);

  const playPlaylist = useCallback((items: PlaylistItem[], startIndex: number = 0) => {
    if (items.length === 0) return;
    setPlaylist(items);
    playTrackFromPlaylist(items, startIndex);
  }, [playTrackFromPlaylist]);

  const playNext = useCallback(() => {
    if (!playlist || playlist.length === 0) return;

    const nextIndex = playlistIndex + 1;
    if (nextIndex < playlist.length) {
      playTrackFromPlaylist(playlist, nextIndex);
    } else if (playlistRepeat) {
      playTrackFromPlaylist(playlist, 0);
    }
  }, [playlist, playlistIndex, playlistRepeat, playTrackFromPlaylist]);

  const playPrevious = useCallback(() => {
    if (!playlist || playlist.length === 0) return;

    // If we're more than 3 seconds in, restart current track
    if (currentTime > 3) {
      seek(0);
      return;
    }

    const prevIndex = playlistIndex - 1;
    if (prevIndex >= 0) {
      playTrackFromPlaylist(playlist, prevIndex);
    } else if (playlistRepeat) {
      playTrackFromPlaylist(playlist, playlist.length - 1);
    }
  }, [playlist, playlistIndex, playlistRepeat, currentTime, seek, playTrackFromPlaylist]);

  const setPlaylistRepeat = useCallback((value: boolean) => {
    setPlaylistRepeatState(value);
  }, []);

  const clearPlaylist = useCallback(() => {
    setPlaylist(null);
    setPlaylistIndex(0);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isPlayerVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (playbackState === 'playing') {
            pause();
          } else {
            resume();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekRelative(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekRelative(10);
          break;
        case 'ArrowUp': {
          e.preventDefault();
          const upIndex = PLAYBACK_RATES.indexOf(playbackRate);
          if (upIndex < PLAYBACK_RATES.length - 1) {
            setPlaybackRate(PLAYBACK_RATES[upIndex + 1]);
          }
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const downIndex = PLAYBACK_RATES.indexOf(playbackRate);
          if (downIndex > 0) {
            setPlaybackRate(PLAYBACK_RATES[downIndex - 1]);
          }
          break;
        }
        case 'a':
          e.preventDefault();
          setLoopPointA();
          break;
        case 'b':
          e.preventDefault();
          setLoopPointB();
          break;
        case 'Escape':
          e.preventDefault();
          if (isFullScreen) {
            setFullScreen(false);
          } else {
            clearLoop();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPlayerVisible, playbackState, playbackRate, isFullScreen, pause, resume, seekRelative, setPlaybackRate, setLoopPointA, setLoopPointB, clearLoop, setFullScreen]);

  const value: PlayerContextType = {
    currentTrack,
    playbackState,
    currentTime,
    duration,
    playbackRate,
    volume,
    loop,
    playlist,
    playlistIndex,
    playlistRepeat,
    isPlayerVisible,
    isFullScreen,
    play,
    pause,
    resume,
    stop,
    seek,
    seekRelative,
    setPlaybackRate,
    setVolume,
    setLoopPointA,
    setLoopPointB,
    clearLoop,
    playPlaylist,
    playNext,
    playPrevious,
    setPlaylistRepeat,
    clearPlaylist,
    setFullScreen,
    closePlayer,
    audioRef,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}

      {/* Global audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={handlePlay}
        onPause={handlePause}
        onWaiting={handleWaiting}
        onCanPlay={handleCanPlay}
        preload="metadata"
      />
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer moet binnen een PlayerProvider worden gebruikt');
  }
  return context;
}

// Helper to format time
export function formatTime(seconds: number): string {
  if (!isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
